import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import { Card, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import Icon from '@components/Icon/Icon';

import { useGetStatuses } from '@hooks/api/status';
import { useGetVitallPatient } from '@hooks/api/vitall-patient';
import { useSession } from '@hooks/useSession';

import { StatusType } from 'generated/types';

import { DashHeader } from '../Common.style';

const messages = defineMessages({
  validatingText: {
    id: 'dashWaiting.validatingText',
    defaultMessage: 'Verifying your ID and consent',
  },
  verifiedText: {
    id: 'dashWaiting.verifiedText',
    defaultMessage: 'ID and consent verified',
  },
  findingText: {
    id: 'dashWaiting.findingText',
    defaultMessage: 'Finding and populating your records',
  },
  findingSubText: {
    id: 'dashWaiting.findingSubText',
    defaultMessage: 'Completed: 6 - 10 days',
  },
  recordStatus: {
    id: 'dashWaiting.recordStatus',
    defaultMessage: 'Profile Status',
  },
});

const DashWaiting: React.FC = () => {
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile },
  } = useSession();
  const { palette } = useTheme();

  const [vitall_patient_id] = [activeProfile?.vitall_patient_id];

  const { data: vitallPatientData, loading: vitallPatientLoading } =
    useGetVitallPatient({ id: vitall_patient_id });
  const { data: statusesData, loading: statusesLoading } = useGetStatuses({
    model: StatusType.VitallPatient_Profile,
  });
  const statuses = statusesData?.filter(({ internal }) => !internal) ?? [];
  const vitallPatient = vitallPatientData?.getVitallPatientRecord;
  const loading = statusesLoading || vitallPatientLoading;

  const currentStatus = vitallPatient?.status;
  const currentStep = currentStatus?.sequence ?? 0;

  if (!currentStatus) {
    return null;
  }

  return (
    <>
      <DashHeader>{formatMessage(messages.recordStatus)}</DashHeader>
      <ContentLoader loading={loading}>
        <Card>
          <Timeline
            sx={{
              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0,
              },
            }}
          >
            {statuses.map(({ color, public_name, sequence }, index) => {
              const step = sequence ?? 0;
              const status =
                step < currentStep
                  ? 'complete'
                  : step > currentStep
                    ? 'incomplete'
                    : 'active';
              const isActive = status === 'active';
              let textColor = color;
              switch (status) {
                case 'complete':
                  textColor = palette.text.primary;
                  break;
                case 'incomplete':
                  textColor = palette.text.secondary;
                  break;
                case 'active':
                default:
                  textColor = color;
                  break;
              }
              return (
                <TimelineItem
                  key={`profile-status-step-${index}`}
                  sx={{
                    minHeight: '48px',
                  }}
                >
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        border: 'none',
                        padding: 0,
                        margin: '4.5px 0',
                        backgroundColor: 'unset',
                      }}
                    >
                      <Icon
                        color={
                          step > currentStep
                            ? 'grey.A700'
                            : (color ?? undefined)
                        }
                        name={step < currentStep ? 'circle-check' : 'circle'}
                        opacity={step !== currentStep ? 0.7 : 1}
                        size="lg"
                        type="fas"
                      />
                    </TimelineDot>
                    {index < statuses.length - 1 && (
                      <TimelineConnector sx={{ width: '1px' }} />
                    )}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: 0.5 }}>
                    <Typography
                      sx={{ color: textColor }}
                      fontWeight={isActive ? 'bold' : undefined}
                      variant="body1"
                    >
                      {public_name}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        </Card>
      </ContentLoader>
    </>
  );
};

export default withErrorBoundary(DashWaiting, { fallback: null });
